import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, CodeBlock, Code, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'overview', title: 'Overview', level: 2 as const },
  { id: 'service-map', title: 'Service map', level: 2 as const },
  { id: 'auth-service', title: 'Auth Service', level: 3 as const },
  { id: 'buzz', title: 'BUZZ', level: 3 as const },
  { id: 'brainer', title: 'Brainer', level: 3 as const },
  { id: 'supporting', title: 'Supporting services', level: 3 as const },
  { id: 'ports', title: 'Default ports', level: 2 as const },
  { id: 'auth-flow', title: 'Authentication flow', level: 2 as const },
]

export function Architecture() {
  return (
    <Article toc={toc}>
      <H1>Architecture</H1>
      <Lead>
        Rabbit is a microservices platform. Each service owns a clear domain and communicates
        with others through REST APIs or the BUZZ event bus.
      </Lead>

      <H2 id="overview">Overview</H2>
      <P>
        All services are stateless HTTP servers backed by PostgreSQL (or SQLite for lightweight services).
        Authentication is centralised in the Auth Service; every other service validates tokens by calling
        Auth's <Code>/api/auth/validate</Code> endpoint before processing a request.
      </P>
      <P>
        The BUZZ event bus decouples services asynchronously. When Mail Service detects a new email,
        it publishes a <Code>email.received</Code> event to BUZZ; any subscriber (e.g. Brainer, Raider Agent)
        can react without Mail knowing who is listening.
      </P>

      <H2 id="service-map">Service map</H2>
      <Screenshot
        alt="Rabbit service dependency diagram"
        caption="Arrows represent synchronous HTTP calls. BUZZ arrows are asynchronous event delivery."
      />

      <CodeBlock language="text" title="Service topology">
{`Frontend (3004)
  └── Auth Service (6000)       — login, token validation, roles, paths
  └── BUZZ (3000)               — events publish/subscribe (REST + gRPC)
  └── Brainer (8000)            — data model, workflows, plates
  └── Mail Service (5000)       — IMAP mailbox monitoring
  └── Storage Service (7000)    — file upload/download

BUZZ ──▶ Mail Service           — triggers on document events
BUZZ ──▶ Brainer                — triggers on custom events
BUZZ ──▶ Raider Agent (AI)      — document parsing via Claude
BUZZ ──▶ Order Parser           — PDF order extraction

Crawl (3020)                    — documentation (no auth required)`}
      </CodeBlock>

      <H2 id="auth-service">Auth Service</H2>
      <H3 id="">Responsibilities</H3>
      <UL>
        <LI>Business registration and tenant isolation</LI>
        <LI>User creation, email verification, and MFA (TOTP)</LI>
        <LI>JWT access + refresh token lifecycle</LI>
        <LI>Role-based access control — roles carry a set of allowed API paths</LI>
        <LI>Token validation endpoint used by all other services</LI>
      </UL>
      <Callout type="info">
        Auth is the only service that handles <strong>business registration</strong>. Creating a new business
        automatically provisions a <Code>business-leader</Code> role with full permissions.
      </Callout>

      <H2 id="buzz">BUZZ — Event Bus</H2>
      <P>
        BUZZ stores events in PostgreSQL and delivers them to registered subscribers via webhooks.
        It exposes both a REST API and a gRPC interface (port 50051) for high-throughput consumers.
      </P>
      <UL>
        <LI><strong>Events</strong> have a type (e.g. <Code>email.received</Code>), a payload, and an optional TTL</LI>
        <LI><strong>Subscriptions</strong> register a callback URL + event type filter</LI>
        <LI>Events are automatically garbage-collected after their TTL expires</LI>
      </UL>

      <H2 id="brainer">Brainer</H2>
      <P>
        Brainer is the largest service. It hosts 13 route modules and has two primary concerns:
      </P>
      <UL>
        <LI><strong>Domain model</strong> — Ingredients, Traits, Cups, CupTraits, CupRelations, Coffees (records), Beans (field values), Flavors (relation links)</LI>
        <LI><strong>Automation</strong> — Preparations (workflow definitions), Steps, Drinkers (scripts), Journeys (workflow instances), Plates (dashboards), Funnels (connectors)</LI>
      </UL>

      <H2 id="supporting">Supporting services</H2>
      <UL>
        <LI><strong>Mail Service</strong> — Polls configured IMAP mailboxes, stores emails and attachments, emits BUZZ events</LI>
        <LI><strong>Storage Service</strong> — Dataset-scoped file storage with role-based permissions and auto-archival</LI>
        <LI><strong>Raider Agent</strong> — Listens for <Code>document.created</Code> events and parses files using Claude AI</LI>
        <LI><strong>Order Parser</strong> — Listens for <Code>document.created</Code> events and extracts structured order data from PDFs</LI>
      </UL>

      <H2 id="ports">Default ports</H2>
      <CodeBlock language="text" title="Default development ports">
{`Auth Service    → http://localhost:6000
BUZZ            → http://localhost:3000  (gRPC: 50051)
Mail Service    → http://localhost:5000
Storage Service → http://localhost:7000
Brainer         → http://localhost:8000
Frontend        → http://localhost:3004
Crawl           → http://localhost:3020`}
      </CodeBlock>

      <H2 id="auth-flow">Authentication flow</H2>
      <P>Every authenticated request follows this sequence:</P>
      <CodeBlock language="text" title="Request authentication flow">
{`1. Client sends request with:
   Authorization: Bearer <access_token>

2. Target service extracts the token from the header

3. Target service calls Auth:
   POST /api/auth/validate
   { token: "<access_token>" }

4. Auth returns: { valid: true, user: { id, businessId, roles, ... } }

5. Target service checks role permissions against the requested path

6. If permitted → process request
   If denied    → 403 Forbidden`}
      </CodeBlock>
      <Callout type="tip">
        Access tokens expire after a short period. The client should use the refresh token to obtain
        a new access token transparently. The frontend handles this automatically via an Axios interceptor.
      </Callout>
    </Article>
  )
}
